import { useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
    ArrowRight, Stethoscope, Building2, Inbox, ScanLine, MessageSquare,
    BarChart3, Wallet, ShieldCheck, Sparkles, Check, GitBranch,
} from "lucide-react";

/**
 * Toothmatrix — umbrella / chooser page for the two products:
 *   · Toothmatrix Practice — Dental Practice Management (clinics, free)
 *   · Toothmatrix Lab      — Dental Lab Management (laboratories, SaaS)
 * Detail pages live at /solutions/dental-practice and /solutions/dental-labs.
 */
export default function Toothmatrix() {
    const nav = useNavigate();
    const { hash } = useLocation();

    // Legacy deep-links (/toothmatrix#pricing) now live on the lab product page.
    useEffect(() => {
        if (hash === "#pricing") nav("/solutions/dental-labs#pricing", { replace: true });
    }, [hash, nav]);

    return (
        <div className="bg-slate-50 min-h-screen text-slate-900" data-testid="toothmatrix-chooser">
            {/* HERO */}
            <section className="relative overflow-hidden bg-gradient-to-br from-[#0B1120] via-[#0e2e3a] to-[#0B1120] text-white">
                <div className="absolute inset-0 opacity-30 [background-image:radial-gradient(circle_at_30%_20%,rgba(14,165,233,0.18),transparent_50%),radial-gradient(circle_at_80%_80%,rgba(56,189,248,0.12),transparent_55%)]" />
                <div className="relative max-w-6xl mx-auto px-6 sm:px-8 pt-20 pb-16 text-center">
                    <div className="font-mono-tr text-[11px] tracking-[0.28em] uppercase text-cyan-300 mb-5">
                        The Toothmatrix family
                    </div>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display tracking-tight leading-[1.05]">
                        One platform.<br /><em className="not-italic text-cyan-400">Two products.</em>
                    </h1>
                    <p className="mt-6 text-lg text-slate-300/95 max-w-2xl mx-auto leading-relaxed">
                        Dental practices send. Dental laboratories produce. Both sides see the
                        same order, live — on the product built for their side of the workflow.
                    </p>
                </div>
            </section>

            {/* PRODUCT CHOOSER */}
            <section className="max-w-6xl mx-auto px-6 sm:px-8 -mt-8 relative z-10 pb-8">
                <div className="grid md:grid-cols-2 gap-6">
                    {/* Practice card */}
                    <div className="rounded-3xl bg-white border border-slate-200 shadow-xl shadow-slate-900/5 p-8 flex flex-col" data-testid="chooser-practice-card">
                        <div className="flex items-center gap-3 mb-5">
                            <div className="w-11 h-11 rounded-xl bg-cyan-50 text-cyan-700 grid place-items-center"><Stethoscope size={20} /></div>
                            <div>
                                <div className="font-display text-[19px] tracking-tight">Toothmatrix Practice</div>
                                <div className="font-mono-tr text-[10px] tracking-[0.2em] uppercase text-cyan-600">Dental Practice Management</div>
                            </div>
                        </div>
                        <p className="text-[14.5px] text-slate-600 leading-relaxed">
                            For dental clinics. One Scan Inbox for every intraoral scanner — send
                            scans, track orders and chat with your lab from a single portal.
                        </p>
                        <ul className="mt-5 space-y-2.5 text-[13.5px] text-slate-700 flex-1">
                            {[
                                ["3Shape · DS Core · Medit · Shining 3D", ScanLine],
                                ["STL / DICOM / PLY uploads — no expiring links", Inbox],
                                ["Live order tracking + direct lab chat", MessageSquare],
                            ].map(([t, Icon]) => (
                                <li key={t} className="flex items-start gap-2.5">
                                    <Icon size={15} className="text-cyan-600 mt-0.5 shrink-0" /> {t}
                                </li>
                            ))}
                        </ul>
                        <div className="mt-5 inline-flex self-start items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11.5px] font-mono-tr tracking-wide">
                            <Check size={11} /> Free for practices
                        </div>
                        <div className="mt-6 flex flex-wrap gap-3">
                            <Link to="/solutions/dental-practice" data-testid="chooser-practice-explore"
                                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white font-medium text-[13.5px] transition">
                                Explore Practice <ArrowRight size={14} />
                            </Link>
                            <Link to="/practice/register" data-testid="chooser-practice-register"
                                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-slate-300 hover:border-cyan-500 text-slate-800 font-medium text-[13.5px] transition">
                                Register your practice
                            </Link>
                        </div>
                    </div>

                    {/* Lab card */}
                    <div className="rounded-3xl bg-[#0B1120] border border-slate-800 shadow-xl shadow-slate-900/20 p-8 flex flex-col text-white" data-testid="chooser-lab-card">
                        <div className="flex items-center gap-3 mb-5">
                            <div className="w-11 h-11 rounded-xl bg-cyan-400/10 text-cyan-300 grid place-items-center border border-cyan-400/20"><Building2 size={20} /></div>
                            <div>
                                <div className="font-display text-[19px] tracking-tight">Toothmatrix Lab</div>
                                <div className="font-mono-tr text-[10px] tracking-[0.2em] uppercase text-cyan-400">Dental Lab Management</div>
                            </div>
                        </div>
                        <p className="text-[14.5px] text-slate-300 leading-relaxed">
                            For dental laboratories. The full operating system — cases, production
                            Kanban, invoicing, prepaid credits, payroll and doctor commissions.
                        </p>
                        <ul className="mt-5 space-y-2.5 text-[13.5px] text-slate-200 flex-1">
                            {[
                                ["Live dashboard, Kanban + WIP limits", BarChart3],
                                ["Invoices, credit float & multi-currency finance", Wallet],
                                ["White-label — your brand, your subdomain", ShieldCheck],
                            ].map(([t, Icon]) => (
                                <li key={t} className="flex items-start gap-2.5">
                                    <Icon size={15} className="text-cyan-300 mt-0.5 shrink-0" /> {t}
                                </li>
                            ))}
                        </ul>
                        <div className="mt-5 inline-flex self-start items-center gap-1.5 px-3 py-1 rounded-full bg-cyan-400/10 border border-cyan-400/25 text-cyan-200 text-[11.5px] font-mono-tr tracking-wide">
                            <Sparkles size={11} /> From $99 / month · usage-based
                        </div>
                        <div className="mt-6 flex flex-wrap gap-3">
                            <Link to="/solutions/dental-labs" data-testid="chooser-lab-explore"
                                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-cyan-400 hover:bg-cyan-300 text-[#0B1120] font-medium text-[13.5px] transition">
                                Explore Lab <ArrowRight size={14} />
                            </Link>
                            <Link to="/pricing/labs" data-testid="chooser-lab-pricing"
                                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-white/20 hover:border-cyan-300 text-white/90 font-medium text-[13.5px] transition">
                                See pricing
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* HOW THEY WORK TOGETHER */}
            <section className="max-w-6xl mx-auto px-6 sm:px-8 py-16">
                <div className="font-mono-tr text-[10.5px] tracking-[0.28em] uppercase text-cyan-700 mb-3 text-center">· Better together</div>
                <h2 className="text-lg md:text-lg font-semibold sm:text-3xl sm:font-light font-display tracking-tight text-center mb-10">
                    One order, two views — always in sync.
                </h2>
                <div className="grid sm:grid-cols-3 gap-5">
                    {[
                        { n: "01", icon: ScanLine, t: "The practice scans & sends", d: "A clinic on Toothmatrix Practice uploads the intraoral scan — or their scanner pushes it automatically." },
                        { n: "02", icon: GitBranch, t: "The lab produces", d: "The order lands in the lab's Scan Inbox on Toothmatrix Lab and flows through the production Kanban." },
                        { n: "03", icon: MessageSquare, t: "Both sides stay in sync", d: "Status changes, messages and files travel between the two products in real time — no WhatsApp, no e-mail chains." },
                    ].map((s) => (
                        <div key={s.n} className="rounded-2xl bg-white border border-slate-200 p-6">
                            <div className="flex items-center justify-between mb-4">
                                <div className="w-9 h-9 rounded-lg bg-cyan-50 text-cyan-700 grid place-items-center"><s.icon size={16} /></div>
                                <span className="font-mono-tr text-[11px] text-slate-300">{s.n}</span>
                            </div>
                            <div className="text-[15px] font-semibold text-slate-900 mb-1.5">{s.t}</div>
                            <p className="text-[13.5px] text-slate-600 leading-relaxed">{s.d}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="bg-[#0B1120] text-white py-16">
                <div className="max-w-3xl mx-auto px-6 sm:px-8 text-center">
                    <h2 className="text-lg md:text-lg font-semibold sm:text-3xl sm:font-light font-display tracking-tight">Not sure which one you need?</h2>
                    <p className="mt-4 text-slate-300/90 leading-relaxed">
                        If you treat patients, start with <strong className="text-white">Practice</strong> — it&rsquo;s free.
                        If you manufacture restorations, book a demo of <strong className="text-white">Lab</strong>.
                    </p>
                    <div className="mt-7 flex flex-wrap justify-center gap-3">
                        <Link to="/practice/register" data-testid="chooser-cta-practice"
                            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-white text-[#0B1120] font-medium text-[14px] hover:bg-cyan-50 transition">
                            <Stethoscope size={15} /> I run a practice
                        </Link>
                        <Link to="/solutions/dental-labs" data-testid="chooser-cta-lab"
                            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-cyan-400 text-[#0B1120] font-medium text-[14px] hover:bg-cyan-300 transition">
                            <Building2 size={15} /> I run a laboratory
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
